import {
  inferColorScheme,
  resolveStorefrontThemeVars,
  type StorefrontTheme,
} from "@loot-os/api-contract";
import { cn } from "@loot-os/ui/lib/utils";
import type { CSSProperties, ReactNode } from "react";

/**
 * Renders its children in a tenant's storefront palette without recoloring the
 * dashboard around it.
 *
 * The storefront applies the same variables to `<html>`; here they go on one
 * container instead. That works because @loot-os/ui's `@theme inline` keeps the
 * *reference* live rather than baking the value — the built CSS really does emit
 * `.bg-card{background-color:var(--card)}` and
 * `.rounded-xl{border-radius:calc(var(--radius) * 1.4)}` — so any utility in the
 * subtree resolves through whatever these variables say here.
 *
 * `--color-border` is the exception, and re-declaring it is load-bearing.
 * Tailwind's base layer sets `*{border-color:var(--color-border)}` while
 * `--color-border:var(--border)` is declared at `:root`. Custom properties
 * substitute at the *declaring* element, so that resolves against the
 * dashboard's palette and inherits as a finished color — a scoped `--border`
 * alone would leave every bare `border` (Card, Table, CardFooter) dashboard-
 * purple on a tenant-colored surface. Re-declaring it here moves the
 * substitution into this subtree.
 *
 * `color-scheme` follows the background's lightness, exactly as the storefront's
 * `applyPageTheme` sets it on `<html>`, so a light palette previews with light
 * native controls here just as shoppers will see them.
 */
export function StorefrontThemeScope({
  theme,
  className,
  children,
}: {
  theme?: StorefrontTheme;
  className?: string;
  children: ReactNode;
}) {
  const { colors, radius, fontFamily } = resolveStorefrontThemeVars(theme);
  const colorScheme = inferColorScheme(colors["--background"]);
  const style = {
    ...colors,
    ...(radius ? { "--radius": radius } : {}),
    ...(fontFamily ? { fontFamily } : {}),
    ...(colorScheme ? { colorScheme } : {}),
    "--color-border": "var(--border)",
  } as CSSProperties;

  // bg-background/text-foreground are explicit: without them the container
  // inherits the dashboard's body colors and only nested surfaces get themed.
  return (
    <div style={style} className={cn("bg-background text-foreground", className)}>
      {children}
    </div>
  );
}
